import { MemoizedSelector, Selector, createSelector } from '@ngrx/store';
import { fromRouter } from 'src/app/store/router';
import { RootState } from 'src/app/utils/store/root_state';
import { FullItemRoute, ItemContentIdentifier, ItemRoute, resultsFetchKey } from 'src/app/models/routing/item-route';
import { Breadcumbs, Item, State, initialState } from './item-content.state';
import { FetchState, errorState, fetchingState, readyState } from 'src/app/utils/state';
import { ItemData } from '../../models/item-data';
import equal from 'fast-deep-equal/es6';
import { Result } from '../../models/attempts';
import { isItemRouteError, ItemRouteError, parseItemUrlSegments } from 'src/app/models/routing/item-route-serialization';
import { fromConfig } from 'src/app/store/config';
import { GroupId } from 'src/app/models/ids';
import { AttemptResolution, attemptResolution } from './attempt-resolution';

interface UserContentSelectors<T extends RootState> {
  selectIsItemContentActive: MemoizedSelector<T, boolean>,
  /**
   * The route error, if any, of the current active content if it is an item
   * To be used by the effect which handles the error
   */
  selectActiveContentRouteError: MemoizedSelector<T, ItemRouteError|null>,
  /**
   * The state of route error handling (if there is an error)
   */
  selectActiveContentRouteErrorHandlingState: MemoizedSelector<T, State['routeErrorHandling']|null>,
  /**
   * If the content is an item and there is no route error: the fully defined route
   */
  selectActiveContentRoute: MemoizedSelector<T, FullItemRoute|null>,
  /**
   * If the content is an item, the current page
   */
  selectActiveContentPage: MemoizedSelector<T, string[]|null>,
  /**
   * If the content is an item: the item id.
   * Warning: this may return an id even when the route is in error state
   */
  selectActiveContentId: MemoizedSelector<T, string|null>,
  /**
   * If the active content is an item: the observed group.
   * `undefined` if not observing
   * `null` if there is no active item
   */
  selectActiveContentObservedGroup: MemoizedSelector<T, ItemRoute['observedGroup']|null>,
  /**
   * If the active content is an item and observation is enabled: the observed-group id.
   * `null` otherwise (no active item OR no observation).
   */
  selectActiveContentObservedGroupId: MemoizedSelector<T, GroupId|null>,
  /**
   * If the active content is an item: the pair (item id, observed-group id) that uniquely
   * identifies the content being viewed. `null` if there is no active item.
   * The output reference is stable across URL changes that do not affect either id, so consumers
   * can rely on `Store.select` to emit only on actual value changes.
   */
  selectActiveContentIdentifier: MemoizedSelector<T, ItemContentIdentifier|null>,
  selectActiveContentItemState: MemoizedSelector<T, State['itemState']>,
  selectActiveContentBreadcrumbsState: MemoizedSelector<T, State['breadcrumbsState']>,
  selectActiveContentResultsState: MemoizedSelector<T, State['resultsState']>,

  /**
   * The active item if we there is one and it has been fetched
   */
  selectActiveContentItem: MemoizedSelector<T, Item|null>,
  /** Whether the active item hides its title header (tabs move to the top bar). */
  selectActiveContentHideHeader: MemoizedSelector<T, boolean>,
  /** Whether the active item shows the platform logo in the top bar when the left menu is hidden. */
  selectActiveContentShowPlatformInsteadOfScore: MemoizedSelector<T, boolean>,
  /**
   * The breadcrumbs of the active item if there is one and it has been fetched
   */
  selectActiveContentBreadcrumbs: MemoizedSelector<T, Breadcumbs|null>,
  /**
   * The results of the active item if there is one and it has been fetched
   */
  selectActiveContentResults: MemoizedSelector<T, Result[]|null>,
  /**
   * The current result of the active item, derived from the route's `attemptId` and the results list
   */
  selectActiveContentCurrentResult: MemoizedSelector<T, Result|null>,
  /**
   * Whether the active item URL still needs an attempt (`pick` / `start`), or `null` when nothing to resolve
   */
  selectAttemptResolution: MemoizedSelector<T, AttemptResolution|null>,

  /**
   * Data that can be used for fetching results
   */
  selectActiveContentInfoForFetchingResults: MemoizedSelector<T, { route: FullItemRoute, item: ItemData['item'] }|null>,
  /**
   * The old structure for sharing the item data. Kept for now for compatibility reasons
   */
  selectActiveContentData: MemoizedSelector<T, FetchState<ItemData>|null>,
}

export function selectors<T extends RootState>(selectState: Selector<T, State>): UserContentSelectors<T> {

  const selectActiveContentRouteParsingResult = createSelector(
    fromRouter.selectSegments,
    fromConfig.selectRedirects,
    (segments, redirects) => (segments ? parseItemUrlSegments(segments, redirects) : null)
  );

  const selectActiveContentRouteParsingResultRoute = createSelector(
    selectActiveContentRouteParsingResult,
    result => (result ? result.route: null)
  );

  const selectActiveContentPage = createSelector(
    selectActiveContentRouteParsingResult,
    result => (result ? result.page: null)
  );

  const selectIsItemContentActive = createSelector(
    selectActiveContentRouteParsingResult,
    result => result !== null
  );

  const selectActiveContentRouteError = createSelector(
    selectActiveContentRouteParsingResultRoute,
    route => (route && isItemRouteError(route) ? route : null)
  );

  const selectActiveContentRouteErrorHandlingState = createSelector(
    selectState,
    selectActiveContentRouteError,
    (state, error) => (error !== null ? state.routeErrorHandling : null)
  );

  const selectActiveContentRoute = createSelector(
    selectActiveContentRouteParsingResultRoute,
    route => (route && !isItemRouteError(route) ? route : null)
  );

  const selectActiveContentId = createSelector(
    selectActiveContentRouteParsingResultRoute,
    route => (route ? route.id ?? null : null)
  );

  const selectActiveContentObservedGroup = createSelector(
    selectActiveContentRouteParsingResultRoute,
    route => (route ? route.observedGroup : null)
  );

  const selectActiveContentObservedGroupId = createSelector(
    selectActiveContentObservedGroup,
    observedGroup => (observedGroup ? observedGroup.id : null)
  );

  // Composed from primitive-returning selectors so ngrx's createSelector preserves the output
  // reference whenever both ids are unchanged (avoiding spurious re-emissions on intra-content
  // URL changes such as query params, fragments, or in-page tab switches).
  const selectActiveContentIdentifier = createSelector(
    selectActiveContentId,
    selectActiveContentObservedGroupId,
    (id, observedGroupId): ItemContentIdentifier|null =>
      (id === null ? null : { id, observedGroupId: observedGroupId ?? undefined })
  );

  const selectActiveContentItemState = createSelector(
    selectState,
    selectActiveContentRoute,
    ({ itemState }, route) =>
      (route && equal(itemState.identifier, { id: route.id, observedGroup: route.observedGroup }) ? itemState : initialState.itemState)
  );

  const selectActiveContentBreadcrumbsState = createSelector(
    selectState,
    selectActiveContentRoute,
    ({ breadcrumbsState }, route) =>
      (route !== null && equal(route, breadcrumbsState.identifier) ? breadcrumbsState : initialState.breadcrumbsState)
  );

  const selectActiveContentResultsState = createSelector(
    selectState,
    selectActiveContentRoute,
    ({ resultsState }, route) =>
      (route !== null && resultsState.identifier !== undefined
        && equal(resultsFetchKey(route), resultsState.identifier) ?
        resultsState : initialState.resultsState)
  );

  const selectActiveContentItem = createSelector(
    selectActiveContentItemState,
    state => state.data ?? null
  );

  const selectActiveContentHideHeader = createSelector(
    selectActiveContentItem,
    item => item?.displaySettings.hideHeader ?? false,
  );

  const selectActiveContentShowPlatformInsteadOfScore = createSelector(
    selectActiveContentItem,
    item => item?.displaySettings.showPlatformInsteadOfScore ?? false,
  );

  const selectActiveContentBreadcrumbs = createSelector(
    selectActiveContentBreadcrumbsState,
    state => state.data ?? null
  );

  const selectActiveContentResults = createSelector(
    selectActiveContentResultsState,
    state => state.data ?? null
  );

  const selectActiveContentCurrentResult = createSelector(
    selectActiveContentRoute,
    selectActiveContentResultsState,
    (route, resultsState) => (route?.attemptId === undefined
      ? null
      : resultsState.data?.find(r => r.attemptId === route.attemptId) ?? null)
  );

  const selectAttemptResolution = createSelector(
    selectActiveContentRoute,
    selectActiveContentItem,
    selectActiveContentResultsState,
    attemptResolution,
  );

  const selectActiveContentInfoForFetchingResults = createSelector(
    selectActiveContentRoute,
    selectActiveContentItemState,
    selectActiveContentBreadcrumbsState,
    (route, itemState, breadcrumbsState) =>
      ((route !== null && itemState.isReady && breadcrumbsState.isReady) ? { route, item: itemState.data } : null)
  );

  const selectActiveContentData = createSelector(
    selectActiveContentRoute,
    selectActiveContentItemState,
    selectActiveContentBreadcrumbsState,
    selectActiveContentResultsState,
    selectActiveContentCurrentResult,
    selectAttemptResolution,
    (route, itemState, breadcrumbsState, resultsState, currentResult, resolution) => {
      if (route === null) return null;
      if (itemState.isError) return errorState(itemState.error);
      if (breadcrumbsState.isError) return errorState(breadcrumbsState.error);
      if (resultsState.isError) return errorState(resultsState.error);
      if (itemState.isFetching || breadcrumbsState.isFetching) return fetchingState<ItemData>();
      if (!resultsState.isReady || resolution !== null) return fetchingState<ItemData>();
      return readyState<ItemData>({
        route,
        item: itemState.data,
        breadcrumbs: breadcrumbsState.data,
        results: resultsState.data,
        currentResult: currentResult ?? undefined,
      });
    }
  );

  return {
    selectIsItemContentActive,
    selectActiveContentRouteError,
    selectActiveContentRouteErrorHandlingState,
    selectActiveContentRoute,
    selectActiveContentPage,
    selectActiveContentId,
    selectActiveContentObservedGroup,
    selectActiveContentObservedGroupId,
    selectActiveContentIdentifier,
    selectActiveContentItemState,
    selectActiveContentBreadcrumbsState,
    selectActiveContentResultsState,
    selectActiveContentItem,
    selectActiveContentHideHeader,
    selectActiveContentShowPlatformInsteadOfScore,
    selectActiveContentBreadcrumbs,
    selectActiveContentResults,
    selectActiveContentCurrentResult,
    selectAttemptResolution,
    selectActiveContentInfoForFetchingResults,
    selectActiveContentData,
  };
}
